// UI と API で共有する定数。DOM/Workers いずれのグローバルにも依存しない純 TS。

// 士業種別。value は DB/コード上のコード、label は日本語表示。
export const PROFESSIONS = [
  { value: "lawyer", label: "弁護士" },
  { value: "tax_accountant", label: "税理士" },
  { value: "labor_consultant", label: "社会保険労務士" },
  { value: "administrative_scrivener", label: "行政書士" },
  { value: "judicial_scrivener", label: "司法書士" },
  { value: "patent_attorney", label: "弁理士" },
  { value: "cpa", label: "公認会計士" },
  { value: "land_surveyor", label: "土地家屋調査士" },
  { value: "sme_consultant", label: "中小企業診断士" },
  { value: "financial_planner", label: "ファイナンシャルプランナー" },
] as const;

export type ProfessionValue = (typeof PROFESSIONS)[number]["value"];

export const PROFESSION_VALUES: readonly string[] = PROFESSIONS.map((p) => p.value);

export function professionLabel(value: string | null | undefined): string {
  return PROFESSIONS.find((p) => p.value === value)?.label ?? "未設定";
}

// 案件ステータス機械(順序どおり)。
export const CASE_STATUSES = [
  "intake",
  "knowledge_ready",
  "strategy_ready",
  "page_plan_ready",
  "approved",
  "awaiting_payment",
  "in_production",
  "published",
  "improving",
] as const;

export type CaseStatus = (typeof CASE_STATUSES)[number];

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  intake: "ヒアリング中",
  knowledge_ready: "情報整理済み",
  strategy_ready: "キーワード設計済み",
  page_plan_ready: "ページ設計済み",
  approved: "承認済み",
  awaiting_payment: "入金待ち",
  in_production: "制作中",
  published: "公開済み",
  improving: "改善運用中",
};

// デザイン6案のバリアント。
export const DESIGN_VARIANTS = [
  { value: "standard", label: "スタンダード" },
  { value: "modern", label: "モダン" },
  { value: "minimal", label: "ミニマル" },
  { value: "natural", label: "ナチュラル" },
  { value: "premium", label: "プレミアム" },
  { value: "creative", label: "クリエイティブ" },
] as const;

export type DesignVariant = (typeof DESIGN_VARIANTS)[number]["value"];

// 料金(すべて税込総額・円)。免税事業者のため消費税区分なし。
export const PRICING = {
  initialFee: 5000, // 初期費用(初回デザイン提案〜公開まで)
  monthlyFee: 0, // 月額管理費(固定費なし。収益は主にクレジット利用)
  initialCreditGrant: 5000, // 初回登録時に付与するクレジット(初期費用と同額で実質無料)
  creditChargeOptions: [3000, 5000, 10000], // クレジットチャージ単位(1cr=1円)
  creditExpiryMonths: 6, // 付与から6ヶ月
} as const;

// AIクレジット消費レート(DESIGN.md §2 の表に統一)。
export const CREDIT_RATES = {
  keywordStrategy: 100, // キーワード戦略生成
  competitorAnalysis: 500, // 競合・価値パターン分析
  monthlyReport: 300, // 月次AIレポート
  improvementProposal: 100, // SEO/CVR改善提案
  blogPer1000Chars: 100, // AIブログ記事生成(1,000文字)
  blogPerExtra500Chars: 50, // 追加500文字ごと
  chatReply: 10, // AIチャットボット応答(1回)
  imageGeneration: 50, // AI画像生成(1枚)
  pageGeneration: 100, // 地域×業務ページ構成生成/ページ設計分析(1回)
  assetUpload: 10, // 素材アップロード(1ファイル)
  assetText: 10, // 素材テキスト入力保存(1回)
  contactSubmission: 500, // 公開サイトからの問い合わせ受信(1件)
  siteBuildRequest: 3000, // デザイン決定後のサイト制作依頼
  designReproposal: 300, // 旧デザイン再提案互換の内部レート
  revisionRequest: 100, // サイト修正依頼(1件)
} as const;

// 振込先(DESIGN.md §2)。
export const BANK_TRANSFER = {
  bankName: "PayPay銀行",
  bankCode: "0033",
  branchName: "ビジネス営業部",
  branchCode: "005",
  accountType: "普通",
  accountNumber: "2074804",
  accountHolder: "カ）パスカリーヌ",
} as const;

export const OPERATOR = {
  companyName: "株式会社Pascaline",
  serviceName: "LegalPages",
} as const;

// 請求種別ラベル(No.15)。金額はすべて税込総額。
export const INVOICE_KIND_LABELS = {
  initial: "初期費用",
  monthly: "月額管理費",
  credit_charge: "クレジットチャージ",
} as const;

export type InvoiceKind = keyof typeof INVOICE_KIND_LABELS;

// 請求ステータスラベル(No.15)。
export const INVOICE_STATUS_LABELS = {
  unpaid: "未入金",
  paid: "入金済み",
  canceled: "取消",
} as const;

export type InvoiceStatus = keyof typeof INVOICE_STATUS_LABELS;

// クレジット台帳の種別ラベル(No.16)。
export const CREDIT_ENTRY_LABELS = {
  grant: "付与",
  consume: "消費",
  adjust: "調整",
  expire: "期限切れ",
} as const;

export type CreditEntryType = keyof typeof CREDIT_ENTRY_LABELS;

// 消費元機能ラベル(credit_ledger.feature)。
export const CREDIT_FEATURE_LABELS: Record<string, string> = {
  keyword_strategy: "キーワード戦略生成",
  competitor_analysis: "競合・価値パターン分析",
  monthly_report: "月次AIレポート",
  improvement: "SEO/CVR改善提案",
  blog: "AIブログ記事生成",
  chat: "AIチャットボット応答",
  image: "AI画像生成",
  page: "地域×業務ページ構成生成",
  asset_upload: "素材アップロード",
  asset_text: "素材テキスト入力",
  contact: "お問い合わせ受信",
  design: "ページ案の再提案",
  site_build_request: "サイト制作依頼",
  revision: "サイト修正依頼",
};

// AIブログ記事の必須生成画像(1記事あたり カバー1枚 + 主要章2枚 = 3枚。DESIGN.md §2)。
export const BLOG_IMAGE_COUNT = 3;
// ブログ画像3枚分の消費(画像1枚=50cr × 3枚 = 150cr)。
export const BLOG_IMAGE_CREDITS = BLOG_IMAGE_COUNT * CREDIT_RATES.imageGeneration;
// プロフィール画像の再生成費用(初回はフロー内で無料、再生成は画像1枚レート=50cr)。
export const PROFILE_IMAGE_CREDITS = CREDIT_RATES.imageGeneration;

// クレジット消費レート表示行(DESIGN.md §2 の表。No.16 で表示)。
export const CREDIT_RATE_ROWS: readonly { feature: string; cost: string }[] = [
  { feature: "キーワード戦略生成", cost: `${CREDIT_RATES.keywordStrategy}cr` },
  { feature: "競合・価値パターン分析", cost: `${CREDIT_RATES.competitorAnalysis}cr〜` },
  { feature: "地域×業務ページ構成生成", cost: `${CREDIT_RATES.pageGeneration}cr〜` },
  { feature: "月次AIレポート", cost: `${CREDIT_RATES.monthlyReport}cr` },
  { feature: "SEO/CVR改善提案", cost: `${CREDIT_RATES.improvementProposal}cr〜` },
  { feature: "AIブログ記事生成（1,000文字）", cost: `${CREDIT_RATES.blogPer1000Chars}cr（追加500文字ごとに +${CREDIT_RATES.blogPerExtra500Chars}cr）` },
  { feature: "ブログ画像（1記事3枚必須）", cost: `${BLOG_IMAGE_CREDITS}cr` },
  { feature: "AIチャットボット応答（1回）", cost: `${CREDIT_RATES.chatReply}cr` },
  { feature: "AI画像生成（1枚）", cost: `${CREDIT_RATES.imageGeneration}cr` },
  { feature: "素材アップロード（1ファイル）", cost: `${CREDIT_RATES.assetUpload}cr` },
  { feature: "素材テキスト入力保存（1回）", cost: `${CREDIT_RATES.assetText}cr` },
  { feature: "お問い合わせ受信（1件）", cost: `${CREDIT_RATES.contactSubmission}cr` },
  { feature: "サイト制作依頼", cost: `${CREDIT_RATES.siteBuildRequest}cr` },
  { feature: "サイト修正依頼（1件）", cost: `${CREDIT_RATES.revisionRequest}cr` },
];

// 顧客サイト(<slug>.pascalinekasane.com)とリポジトリ(site-<slug>)の参照(No.14)。
export const SITE = {
  domainSuffix: "pascalinekasane.com",
  repoOrg: "Pascaline-Homepages",
} as const;

export function siteUrl(slug: string | null | undefined): string | null {
  return slug ? `https://${slug}.${SITE.domainSuffix}` : null;
}

export function repoUrl(slug: string | null | undefined): string | null {
  return slug ? `https://github.com/${SITE.repoOrg}/site-${slug}` : null;
}

// 士業種別ごとの対応分野リスト(No.3 でチェックボックス表示)。
// モックNo.3 の分野は弁護士用(DESIGN.md §3)。他士業は代表的な分野で構成。
export const PRACTICE_AREAS_BY_PROFESSION: Record<ProfessionValue, readonly string[]> = {
  lawyer: [
    "企業法務", "相続・遺言", "交通事故", "離婚・男女問題", "債務整理",
    "労働問題", "刑事事件", "不動産・建築", "知的財産", "その他",
  ],
  tax_accountant: [
    "法人税務顧問", "確定申告", "相続税・贈与税", "会社設立支援", "記帳代行",
    "事業承継", "資金調達支援", "補助金・助成金", "税務調査対応", "その他",
  ],
  labor_consultant: [
    "労務相談", "就業規則作成", "給与計算", "社会保険手続き", "助成金申請",
    "労使トラブル対応", "人事制度構築", "障害年金", "ハラスメント対策", "その他",
  ],
  administrative_scrivener: [
    "建設業許可", "産廃収集運搬業許可", "在留・ビザ申請", "相続・遺言", "車庫証明",
    "古物商許可", "会社設立", "農地転用", "補助金申請", "その他",
  ],
  judicial_scrivener: [
    "不動産登記", "商業・法人登記", "相続手続き", "遺言・成年後見", "債務整理",
    "会社設立", "供託手続き", "帰化申請サポート", "裁判事務", "その他",
  ],
  patent_attorney: [
    "特許出願", "商標登録", "意匠登録", "実用新案", "知的財産コンサル",
    "権利侵害対応", "外国出願", "ライセンス契約", "先行技術調査", "その他",
  ],
  cpa: [
    "会計監査", "財務コンサル", "IPO支援", "M&A支援", "内部統制構築",
    "事業計画策定", "税務顧問", "会計システム導入", "企業再生支援", "その他",
  ],
  land_surveyor: [
    "土地境界確定", "建物表題登記", "分筆・合筆登記", "地目変更登記", "土地測量",
    "境界紛争対応", "用地測量", "現況測量", "登記相談", "その他",
  ],
  sme_consultant: [
    "経営診断", "事業計画策定", "補助金・助成金支援", "資金調達支援", "販路開拓支援",
    "事業承継支援", "業務改善", "創業支援", "事業再構築", "その他",
  ],
  financial_planner: [
    "家計相談", "保険見直し", "資産運用相談", "住宅ローン相談", "相続・贈与相談",
    "教育資金設計", "老後資金設計", "ライフプラン設計", "確定拠出年金", "その他",
  ],
};

export function practiceAreasFor(profession: string | null | undefined): readonly string[] {
  if (profession && profession in PRACTICE_AREAS_BY_PROFESSION) {
    return PRACTICE_AREAS_BY_PROFESSION[profession as ProfessionValue];
  }
  return PRACTICE_AREAS_BY_PROFESSION.lawyer;
}

// サイトの雰囲気(No.3)。value はコード、label/description は表示。
export const SITE_ATMOSPHERES = [
  { value: "trust", label: "信頼感・堅実", description: "落ち着きと信頼感のある印象" },
  { value: "smart", label: "先進的・スマート", description: "洗練されたモダンな印象" },
  { value: "friendly", label: "親しみやすい", description: "やさしく親しみやすい印象" },
  { value: "stylish", label: "スタイリッシュ", description: "高級感のあるスタイリッシュな印象" },
] as const;

export type SiteAtmosphere = (typeof SITE_ATMOSPHERES)[number]["value"];

// 対応地域(No.3)。
export const SERVICE_REGIONS = [
  "福岡市", "北九州市", "久留米市", "飯塚市", "春日市", "大野城市",
  "筑紫野市", "糸島市", "宗像市", "行橋市", "福岡県全域", "全国対応",
] as const;

// No.9 サイト構成の標準ページ一覧。required=必須ページ。
export const SITE_PAGES = [
  { key: "top", label: "トップ", required: true },
  { key: "services", label: "取扱業務", required: true },
  { key: "pricing", label: "料金案内", required: true },
  { key: "about", label: "事務所紹介", required: true },
  { key: "profile", label: "スタッフ紹介", required: false },
  { key: "voice", label: "お客様の声", required: false },
  { key: "blog", label: "ブログ", required: false },
  { key: "faq", label: "よくある質問", required: false },
  { key: "contact", label: "お問い合わせ", required: true },
] as const;

// アップロード素材の種別(uploads.kind と対応)。
export const UPLOAD_KINDS = [
  { value: "face", label: "本人の顔写真", required: true, hint: "さまざまな表情・角度の写真をアップロードしてください。" },
  { value: "office", label: "事務所写真", required: false, hint: "オフィスの雰囲気や設備が伝わる写真をアップロードしてください。" },
  { value: "logo", label: "ロゴ", required: false, hint: "ロゴデータがあればアップロードしてください。" },
  { value: "testimonial", label: "お客様の声", required: false, hint: "掲載許可済みの声やアンケート画像があればアップロードしてください。" },
  { value: "fee_table", label: "料金表", required: false, hint: "料金表や見積もり条件が分かる資料をアップロードしてください。" },
  { value: "document", label: "面談資料・パンフレット", required: false, hint: "文字起こし、名刺、パンフレットなどをアップロードできます。" },
  { value: "other", label: "その他資料", required: false, hint: "パンフレット・名刺・使いたい画像など。AIが内容を読み取り反映します。" },
] as const;

export type UploadKind = (typeof UPLOAD_KINDS)[number]["value"];
export const UPLOAD_KIND_VALUES: readonly string[] = UPLOAD_KINDS.map((k) => k.value);

// デザイン再提案の消費クレジット(DESIGN.md §2)。
export const REPROPOSAL_CREDITS = CREDIT_RATES.designReproposal;

// AIブログの目安文字数オプション(No.11 記事生成フォーム)。
export const BLOG_CHAR_OPTIONS = [1000, 1500, 2000] as const;
export type BlogCharOption = (typeof BLOG_CHAR_OPTIONS)[number];

// 目安文字数からブログ記事生成の消費クレジットを算出(DESIGN.md §2)。
// 1,000字=100cr、以降 追加500字ごとに +50cr(1,500字=150cr / 2,000字=200cr)。
export function blogCreditCost(targetChars: number): number {
  const extraBlocks = Math.max(0, Math.ceil((targetChars - 1000) / 500));
  return CREDIT_RATES.blogPer1000Chars + extraBlocks * CREDIT_RATES.blogPerExtra500Chars;
}

// ブログ記事生成の総消費 = 記事テキスト + 必須画像3枚(150cr)。
// 見積表示・実消費の両方でこの合計を使う(例: 1,000字 = 100 + 150 = 250cr)。
export function blogTotalCreditCost(targetChars: number): number {
  return blogCreditCost(targetChars) + BLOG_IMAGE_CREDITS;
}

// AIチャットボットの1セッション1日あたり応答上限の既定値(env CHAT_DAILY_LIMIT で上書き)。
export const CHAT_DAILY_LIMIT_DEFAULT = 30;

// IP単位の1日あたり応答上限の既定値(env CHAT_IP_DAILY_LIMIT で上書き)。
// 訪問者Cookie(lp_widget_sid)を毎回捨てる乱用に対する多層キャップの1つ。
export const CHAT_IP_DAILY_LIMIT_DEFAULT = 50;

// テナント単位の1日あたり応答総量上限の既定値(env CHAT_TENANT_DAILY_LIMIT で上書き)。
// 複数IP/Cookieを跨いだクレジット枯渇攻撃に対する総量キャップ。
export const CHAT_TENANT_DAILY_LIMIT_DEFAULT = 200;
